import type { CodeLang } from "@/lib/code-lang";

const PAGE_ROOT_SELECTOR = "[data-page-path]";
const codeLangPublicationOwners = new WeakMap<CodeLangAttributeRoot, object>();

interface CodeLangQueryScope {
  querySelector(selector: string): unknown;
}

interface LanguageToggleMarker {
  closest(selector: string): CodeLangQueryScope | null;
}

interface CodeLangAttributeRoot {
  setAttribute(name: string, value: string): void;
  removeAttribute(name: string): void;
}

/**
 * LanguageToggle marker가 속한 현재 글 안에서 실제 언어 블록만 찾는다.
 * App Router 전환 중 이전 글 DOM이 함께 남아 있어도 현재 글 밖의 블록은 보지 않는다.
 */
export function detectPresentCodeLangs(
  marker: LanguageToggleMarker | null,
  availableLangs: readonly CodeLang[],
): CodeLang[] {
  const pageRoot = marker?.closest(PAGE_ROOT_SELECTOR);
  if (!pageRoot) return [];

  return availableLangs.filter((lang) =>
    Boolean(pageRoot.querySelector(`.${lang}-only`)),
  );
}

/**
 * 출석 UI가 구독하는 언어 목록을 게시한다.
 * 새 페이지가 root를 인계받은 뒤에는 이전 페이지 cleanup이 새 값을 지우지 못한다.
 */
export function publishCodeLangs(
  root: CodeLangAttributeRoot,
  langs: readonly CodeLang[],
): () => void {
  const owner = {};
  codeLangPublicationOwners.set(root, owner);

  if (langs.length < 2) {
    root.removeAttribute("data-code-langs");
  } else {
    root.setAttribute("data-code-langs", langs.join(","));
  }

  return () => {
    if (codeLangPublicationOwners.get(root) !== owner) return;

    codeLangPublicationOwners.delete(root);
    root.removeAttribute("data-code-langs");
  };
}
